import {
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash-es/debounce';

import { icon_chevron_down } from '@gn-audio/icons';
import { JabraIcon, useFallbackTranslation, useOutsideAlerter } from '@gn-audio/jss-components';
import { Option } from '@gn-audio/jss-core/common/models';

type DropdownPosition = 'left' | 'right';
export interface JabraDropdownOption extends Option {
  id?: string;
  icon?: ReactElement;
}
interface ActiveOption {
  id: string;
  index: number;
}

interface JabraDropdownProps {
  defaultTitle?: string;
  selectedOption?: JabraDropdownOption | null;
  options: JabraDropdownOption[];
  onOptionSelect: (option: JabraDropdownOption) => void;
  containerClassNames?: string;
  dropdownTriggerClassNames?: string;
  itemsContainerClassNames?: string;
  itemClassNames?: string;
  label?: string;
  disabled?: boolean;
  searchInput?: boolean;
}

const JabraDropdown = ({
  defaultTitle = 'Select an option',
  selectedOption,
  options,
  onOptionSelect,
  containerClassNames,
  dropdownTriggerClassNames,
  itemsContainerClassNames,
  itemClassNames,
  label = 'Dropdown menu',
  disabled = false,
  searchInput = false,
}: JabraDropdownProps) => {
  const { t } = useFallbackTranslation();
  const dropdownId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedOption, setFocusedOption] = useState<ActiveOption | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>('left');
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [isMeasured, setIsMeasured] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const focusedClassNames =
    'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus';

  const filteredOptions: JabraDropdownOption[] = useMemo(
    () =>
      searchInput
        ? options.filter((option) => option.title.toLowerCase().includes(searchQuery.toLowerCase()))
        : options,
    [options, searchInput, searchQuery]
  );

  const scrollToActiveElement = useCallback((index: number) => {
    if (listItemRefs.current[index]) {
      listItemRefs.current[index]?.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      });
    }
  }, []);

  useEffect(() => {
    if (dropdownContainerRef.current) {
      const width = dropdownContainerRef.current.offsetWidth;
      setDropdownWidth(width);
      setIsMeasured(true);
    }
  }, []);

  const openDropdown = useCallback(() => {
    let position: DropdownPosition = 'left';

    if (triggerRef.current && typeof window !== 'undefined') {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const spaceOnRight = viewportWidth - triggerRect.left;

      if (dropdownWidth > triggerRect.width) {
        if (spaceOnRight < dropdownWidth) {
          position = 'right';
        } else {
          position = 'left';
        }
      }
    }

    setDropdownPosition(position);
    setIsOpen(true);

    setTimeout(() => {
      const selectedIndex = selectedOption
        ? filteredOptions.findIndex((option) => option.value === selectedOption.value)
        : -1;

      if (searchInput && searchInputRef.current) {
        searchInputRef.current.focus();
        if (filteredOptions.length > 0) {
          const initialIndex = selectedIndex >= 0 ? selectedIndex : 0;
          setFocusedOption({
            id: filteredOptions[initialIndex]?.id ?? filteredOptions[initialIndex].key,
            index: initialIndex,
          });
          scrollToActiveElement(initialIndex);
        }
      } else {
        if (filteredOptions.length > 0 && listItemRefs.current[0]) {
          const initialIndex = selectedIndex >= 0 ? selectedIndex : 0;
          listItemRefs.current[initialIndex]?.focus();
          setFocusedOption({
            id: filteredOptions[initialIndex]?.id ?? filteredOptions[initialIndex].key,
            index: initialIndex,
          });
          scrollToActiveElement(initialIndex);
        }
      }
    }, 0);
  }, [dropdownWidth, selectedOption, filteredOptions, searchInput, scrollToActiveElement]);

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openDropdown();
    }
  }, [isOpen, openDropdown]);

  const handleOptionClick = useCallback(
    (option: JabraDropdownOption) => {
      onOptionSelect(option);
      setIsOpen(false);
      triggerRef.current?.focus();
      setSearchQuery('');
    },
    [onOptionSelect]
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setFocusedOption(null);
    setSearchQuery('');
  }, []);

  useOutsideAlerter(dropdownRef, closeMenu);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = debounce(() => {
      closeMenu();
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [isOpen, closeMenu]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent, fromOption?: JabraDropdownOption) => {
      if (isOpen && !['Escape', 'ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(e.key)) {
        return;
      }

      if (['Escape', 'ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
      }

      switch (e.key) {
        case 'Tab':
          if (isOpen && fromOption) {
            const currentIndex = focusedOption?.index ?? -1;

            if (currentIndex === filteredOptions.length - 1) {
              closeMenu();
            }
          }
          break;

        case 'Escape':
          closeMenu();
          triggerRef.current?.focus();
          break;

        case 'ArrowDown':
        case 'ArrowUp': {
          if (!isOpen && e.key === 'ArrowDown') {
            toggleDropdown();
            break;
          }

          if (!isOpen) break;

          const currentIndex = focusedOption?.index ?? -1;
          const direction = e.key === 'ArrowDown' ? 1 : -1;
          let nextIndex = currentIndex + direction;
          if (nextIndex < 0) nextIndex = filteredOptions.length - 1;
          if (nextIndex >= filteredOptions.length) nextIndex = 0;

          if (filteredOptions.length > 0) {
            listItemRefs.current[nextIndex]?.focus();
            setFocusedOption({
              id: filteredOptions[nextIndex]?.id || filteredOptions[nextIndex]?.key,
              index: nextIndex,
            });
            scrollToActiveElement(nextIndex);
          }
          break;
        }

        case 'Enter':
        case ' ':
          if (!isOpen) {
            toggleDropdown();
          } else if (fromOption) {
            handleOptionClick(fromOption);
          }
          break;
      }
    },
    [
      closeMenu,
      isOpen,
      focusedOption?.index,
      filteredOptions,
      toggleDropdown,
      handleOptionClick,
      scrollToActiveElement,
    ]
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Tab':
          closeMenu();
          break;

        case 'Escape':
          closeMenu();
          triggerRef.current?.focus();
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (filteredOptions.length > 0) {
            const currentIndex = focusedOption?.index ?? -1;
            const nextIndex = currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : 0;
            setFocusedOption({
              id: filteredOptions[nextIndex]?.id ?? filteredOptions[nextIndex].key,
              index: nextIndex,
            });
            scrollToActiveElement(nextIndex);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (filteredOptions.length > 0) {
            const currentIndex = focusedOption?.index ?? -1;
            const nextIndex = currentIndex > 0 ? currentIndex - 1 : filteredOptions.length - 1;
            setFocusedOption({
              id: filteredOptions[nextIndex]?.id ?? filteredOptions[nextIndex].key,
              index: nextIndex,
            });
            scrollToActiveElement(nextIndex);
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (focusedOption !== null && filteredOptions[focusedOption.index]) {
            handleOptionClick(filteredOptions[focusedOption.index]);
          }
          break;
      }
    },
    [closeMenu, filteredOptions, scrollToActiveElement, focusedOption, handleOptionClick]
  );

  return (
    <div
      ref={dropdownRef}
      className={classNames(
        containerClassNames,
        'relative flex flex-grow cursor-pointer items-center justify-between'
      )}
    >
      <button
        ref={triggerRef}
        className={classNames(
          dropdownTriggerClassNames,
          focusedClassNames,
          'dropdown-trigger flex w-full cursor-pointer items-center justify-between border-0 bg-transparent p-0 text-left disabled:cursor-not-allowed disabled:opacity-50'
        )}
        role="combobox"
        aria-controls={`${dropdownId}-listbox`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-activedescendant={focusedOption ? `${dropdownId}-${focusedOption.id}` : undefined}
        type="button"
        disabled={disabled}
      >
        <div className="flex items-center">
          {selectedOption?.icon && <span className="flex">{selectedOption.icon}</span>}
          <span>{selectedOption?.title || defaultTitle}</span>
        </div>
        <JabraIcon
          className={`ml-2 h-5 w-5 transform transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          Icon={icon_chevron_down}
          aria-hidden="true"
        />
      </button>

      <div
        ref={dropdownContainerRef}
        className={classNames(
          itemsContainerClassNames,
          'absolute top-full z-50 m-auto mt-1 min-w-[100%] rounded-md bg-white shadow-lg',
          {
            block: isOpen,
            hidden: !isOpen && isMeasured,
            'pointer-events-none invisible': !isMeasured,
            'left-0': dropdownPosition === 'left',
            'right-0': dropdownPosition === 'right',
          }
        )}
      >
        {searchInput && (
          <input
            ref={searchInputRef}
            role="searchbox"
            type="search"
            className="sticky top-0 z-10 w-full rounded-t-md border bg-white px-4 py-2"
            placeholder={`${t('system.Search')}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            aria-activedescendant={focusedOption ? `${dropdownId}-${focusedOption.id}` : undefined}
            autoComplete="off"
            onBlur={(e) => {
              if (
                isOpen &&
                searchInput &&
                !dropdownRef.current?.contains(e.relatedTarget as Node)
              ) {
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }
            }}
          />
        )}
        <div
          id={`${dropdownId}-listbox`}
          role="listbox"
          aria-label={label}
          className="max-h-80 overflow-auto"
          tabIndex={-1}
        >
          {filteredOptions.map((option, index) => (
            <div
              id={`${dropdownId}-${option.id ?? option.key}`}
              key={option.value}
              ref={(el) => {
                if (listItemRefs.current) {
                  listItemRefs.current[index] = el;
                }
              }}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(e) => handleKeyDown(e, option)}
              onFocus={() => {
                setFocusedOption({ id: option.id ?? option.key, index: index });
                scrollToActiveElement(index);
              }}
              className={classNames(
                itemClassNames,
                focusedClassNames,
                'flex cursor-pointer items-center whitespace-nowrap rounded-md px-4 py-2 hover:bg-gray-200',
                {
                  'bg-gray-100': selectedOption?.value === option.value,
                  '!bg-blue-100': searchInput && focusedOption?.index === index,
                }
              )}
              role="option"
              aria-selected={selectedOption?.value === option.value}
            >
              {option.icon}
              {option.title}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JabraDropdown;
